import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import classNames from 'classnames';
import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline';
import HelpOutline from '@mui/icons-material/HelpOutline';
import { AccessConfirmationData } from './Security';
import { handleAutofocus } from '../../../lib/TextField';
import { useI18n } from '../../../lib/hook/useI18n';
import { isVendorMode } from '../../../lib/Vendor';
import CountDownTimer from '../../RecorderPlayer/CountDownTimer';

const RESET_RESEND_PIN = 30000;

const useStyles = makeStyles((theme: Theme) => ({
  dialogContent: {
    '&:first-child': {
      paddingTop: theme.spacing(0),
    },
  },
  pinError: {
    fontSize: theme.spacing(1.625),
    fontWeight: 500,
    lineHeight: theme.spacing(3),
  },
  pinSent: {
    color: 'var(--success-color-dark)',
    marginRight: theme.spacing(0.3),
  },
  textField: {
    fontSize: theme.spacing(4),
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: theme.spacing(4.75),
    padding: theme.spacing(1.625, 2),
    textAlign: 'center',
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(1.625, 0.75),
    },
  },
  buttonNoTransform: {
    textTransform: 'none',
  },
}));

type PinCode = Record<string, string>;

interface Props {
  email: string;
  hasError: boolean;
  loading: boolean;
  onResendPin: (data: AccessConfirmationData) => void;
  onSubmit: (data: AccessConfirmationData, onError?: () => void) => void;
  onWrongEmail: () => void;
  open: boolean;
  passcode: string;
}

const VerifyPinCodeDialog: React.FC<Props> = (props: Props): JSX.Element => {
  const { t } = useI18n();
  const classes = useStyles();

  const isVendor = isVendorMode();
  const shouldUseUnifiedConsent = isVendor;

  const {
    email,
    hasError,
    loading,
    onResendPin,
    onSubmit,
    onWrongEmail,
    open,
    passcode,
  } = props;

  const [newPinSent, setNewPinSent] = useState(false);
  const [nextFocus, setNextFocus] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [verificationCode, setVerificationCode] = useState<PinCode>({});

  const verificationCodeNumbers = useMemo(
    () => Object.values(verificationCode).join(''),
    [verificationCode]
  );

  const disabled = verificationCodeNumbers.length < 6 || loading || submitted;

  useEffect(() => {
    if (open) {
      setNewPinSent(false);
      setNextFocus(0);
      setVerificationCode({});
      setSubmitted(false);
    }
  }, [open]);

  const onVerificationCodeChange = (
    e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
    key: number
  ) => {
    // only allow numbers
    const value = e.target.value.trim().replace(/[^0-9]/g, '');

    setVerificationCode((prev: PinCode) => {
      const newPrev = { ...prev };
      newPrev[key] = value;
      return newPrev;
    });

    setNextFocus(value.length ? key + 1 : key);
  };

  // handle the paste action
  const onInputPaste = (
    event: React.ClipboardEvent<HTMLTextAreaElement | HTMLInputElement>,
    key: number
  ) => {
    // only paste numbers
    const pin = event.clipboardData
      .getData('text/plain')
      .trim()
      .replace(/[^0-9]/g, '')
      .split('');

    // only paste full pin for 1st textfield
    if (key !== 0 || !pin.length) {
      return;
    }

    const pastedPin: PinCode = pin.reduce(
      (current, value, key) => ({ ...current, [key]: value }),
      {}
    );

    setVerificationCode(pastedPin);
    setNextFocus(pin.length - 1);

    // if pasted pin fill the boxes, auto-submit
    if (pin.length === 6) {
      setSubmitted(true);
      onSubmit(
        {
          passcode,
          email,
          verificationCode: pin.join(''),
        },
        handleOnError
      );
    }
  };

  // if error is returned from onSubmit, set false submitted
  const handleOnError = () => setSubmitted(false);

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    e.stopPropagation();
    setSubmitted(true);

    onSubmit(
      { passcode, email, verificationCode: verificationCodeNumbers },
      handleOnError
    );
  };

  const handleResendPin = () => {
    // send reset pin code
    onResendPin({ passcode, email });
    // clear verification code
    setVerificationCode({});
    // display new pin sent text
    setNewPinSent(true);
    // display Resend PIN action after 30 seconds
    setTimeout(() => {
      setNewPinSent(false);
    }, RESET_RESEND_PIN);
  };

  const renderSubtitle = (): JSX.Element => (
    <Box mb={5}>
      <Typography color="textSecondary">
        {t('room.verify.modal.body')}
      </Typography>
      <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }}>
        <Typography
          color="textPrimary"
          component="span"
          style={{ marginRight: 8 }}
        >
          {email}
        </Typography>
        <Typography
          color="secondary"
          onClick={onWrongEmail}
          variant="body1"
          style={{ cursor: 'pointer' }}
        >
          {t('room.verify.modal.wrong_email')}
        </Typography>
      </Box>
    </Box>
  );

  const renderTextCodes = (): JSX.Element => (
    <Box display="flex">
      {[...Array(6).keys()].map((key) => (
        <Box key={key} mr={{ xs: 1, sm: key === 2 ? 4 : 1 }}>
          <TextField
            color="secondary"
            disabled={loading || submitted}
            error={hasError}
            fullWidth
            inputProps={{
              className: classes.textField,
              // open number pad in mobile
              inputMode: 'numeric',
              maxLength: 1,
              onPaste: (e) => onInputPaste(e, key),
            }}
            inputRef={handleAutofocus(key === nextFocus, false)}
            onChange={(e) => onVerificationCodeChange(e, key)}
            required
            value={verificationCode[key] || ''}
            variant="outlined"
          />
        </Box>
      ))}
    </Box>
  );

  const renderPinError = (): JSX.Element | undefined => {
    if (!hasError) {
      return;
    }

    return (
      <Typography className={classes.pinError} color="error">
        {t('room.verify.modal.incorrect_pin')}
      </Typography>
    );
  };

  const renderResendPin = (): JSX.Element => {
    if (newPinSent) {
      return (
        <Box alignItems="center" display="flex" mt={2}>
          <Typography className={classes.pinSent}>
            <CheckCircleOutline color="inherit" fontSize="small" />{' '}
            {t('room.verify.modal.pin_sent')}
          </Typography>
          <CountDownTimer time={30}>
            {(counter: number) => (
              <Typography color="textSecondary">
                {t('room.verify.modal.resend_label', { counter })}
              </Typography>
            )}
          </CountDownTimer>
        </Box>
      );
    }
    return (
      <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} mt={2}>
        <Box
          alignItems="center"
          display="flex"
          width={{ xs: '100%', md: 'auto' }}
        >
          <Typography color="textSecondary">
            {t('room.verify.modal.get_pin')}
          </Typography>
          <Box display="flex" ml={1} mr={1}>
            <Tooltip
              arrow
              placement="top"
              PopperProps={{ disablePortal: true }}
              title={t('room.verify.modal.tooltip')}
              enterTouchDelay={0}
              leaveTouchDelay={5000}
            >
              <HelpOutline
                fontSize="small"
                sx={(theme) => ({ color: theme.palette.text.secondary })}
              />
            </Tooltip>
          </Box>
        </Box>

        <Typography
          color="secondary"
          onClick={handleResendPin}
          variant="body1"
          style={{ cursor: 'pointer' }}
        >
          {t('room.verify.modal.reset_pin')}
        </Typography>
      </Box>
    );
  };

  return (
    <Dialog
      BackdropProps={{
        style: {
          backdropFilter: 'blur(4px)',
          backgroundColor: 'var(--dialog-background-color-dark)',
        },
      }}
      fullWidth
      maxWidth="xs"
      open={open}
    >
      <DialogTitle>{t('room.verify.modal.title')}</DialogTitle>
      <form onSubmit={handleSubmit} tabIndex={-1}>
        <DialogContent
          classes={{
            root: classes.dialogContent,
          }}
        >
          {renderSubtitle()}
          {renderTextCodes()}
          {renderPinError()}
          {renderResendPin()}
        </DialogContent>
        <DialogActions>
          <Button
            className={classNames({
              [classes.buttonNoTransform]: shouldUseUnifiedConsent,
            })}
            color="secondary"
            disabled={loading || disabled}
            type="submit"
            variant="contained"
          >
            {shouldUseUnifiedConsent
              ? t('room.verify.modal.newer_button')
              : t('room.verify.modal.button')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default VerifyPinCodeDialog;
